/**
 * Pure helpers for the Visual Story Board — id generation, node/section
 * factories, and the derived (never-persisted) render state used for the
 * character-filter "mute, don't hide" effect and the Timeline View.
 */

import { NODE_TYPE_MAP, DEFAULT_NODE_SIZE } from '../constants';

export const makeId = (prefix) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export function createDefaultBoard(storyId) {
  const now = Date.now();
  return {
    id: makeId('board'),
    storyId,
    sections: [],
    nodes: [],
    connections: [],
    createdAt: now,
    updatedAt: now,
  };
}

export function createDefaultNode(type, position) {
  const config = NODE_TYPE_MAP[type];
  return {
    id: makeId('node'),
    type,
    title: `New ${config?.label ?? 'Card'}`,
    description: '',
    position: position ?? { x: 0, y: 0 },
    status: 'idea',
    chapterId: null,
    characterIds: [],
    sectionId: null,
    metadata: {},
    createdAt: Date.now(),
  };
}

export function createDefaultSection(position) {
  return {
    id: makeId('section'),
    title: 'New Section',
    type: 'custom',
    position: position ?? { x: 0, y: 0 },
    size: { width: 640, height: 420 },
  };
}

/**
 * Which node ids should render at full opacity when a character filter is
 * active: the selected character(s) themselves, anything directly connected
 * to them, and anything that lists them in `characterIds`. Never mutates
 * `nodes`/`connections`, never written back to context — this is purely a
 * per-render derived Set.
 */
export function computeHighlightSet(nodes, connections, selectedCharacterIds) {
  const highlighted = new Set();
  if (!selectedCharacterIds?.length) return highlighted;

  const selected = new Set(selectedCharacterIds);
  selected.forEach((id) => highlighted.add(id));

  nodes.forEach((node) => {
    if (node.characterIds?.some((id) => selected.has(id))) highlighted.add(node.id);
  });

  // Walk connections to a fixed point rather than a single pass, so
  // highlighting a connected chain (A-B-C) doesn't depend on which order
  // the edges happen to appear in the array (e.g. the order they were drawn).
  let changed = true;
  while (changed) {
    changed = false;
    connections.forEach((conn) => {
      if (highlighted.has(conn.sourceNodeId) && !highlighted.has(conn.targetNodeId)) {
        highlighted.add(conn.targetNodeId);
        changed = true;
      }
      if (highlighted.has(conn.targetNodeId) && !highlighted.has(conn.sourceNodeId)) {
        highlighted.add(conn.sourceNodeId);
        changed = true;
      }
    });
  }

  return highlighted;
}

/** Connection ids whose source or target is in `highlightSet` — used to keep matching edges from muting too. */
export function computeHighlightedConnectionIds(connections, highlightSet) {
  if (!highlightSet.size) return new Set();
  return new Set(
    connections
      .filter((c) => highlightSet.has(c.sourceNodeId) && highlightSet.has(c.targetNodeId))
      .map((c) => c.id)
  );
}

/**
 * react-flow requires a parent (group/section) node to appear before its
 * children in the nodes array.
 */
export function sortNodesForReactFlow(nodes) {
  return [...nodes].sort((a, b) => {
    const aIsSection = a.__section ? 0 : 1;
    const bIsSection = b.__section ? 0 : 1;
    return aIsSection - bIsSection;
  });
}

/**
 * Chapters in manuscript order, each with the board nodes linked to it
 * (`node.chapterId === chapter.id`) — feeds the Timeline View.
 */
export function getTimelineData(story, board) {
  if (!story) return [];
  const nodesByChapter = new Map();
  (board?.nodes ?? []).forEach((node) => {
    if (!node.chapterId) return;
    if (!nodesByChapter.has(node.chapterId)) nodesByChapter.set(node.chapterId, []);
    nodesByChapter.get(node.chapterId).push(node);
  });

  return story.chapters.map((chapter) => ({
    chapter,
    nodes: nodesByChapter.get(chapter.id) ?? [],
  }));
}

export function nodeDefaultSize() {
  return { ...DEFAULT_NODE_SIZE };
}
